/** @jsxImportSource @emotion/react */

import {
  Header,
  useSession,
  EmptyContent,
  Balances,
  TruncatedString,
  formatCurrency,
} from '@quickbit/backoffice-core-frontend';
import {
  Typography,
  createStyles,
  Icon,
  Button,
  useTheme,
  Table,
  Pagination,
  DateInput,
  UtilityButton,
} from '@quickbit/qb-design-system';
import { useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAsync, useAsyncFn, useUpdateEffect } from 'react-use';
import { getMerchant } from 'tabs/api';
import { Merchant } from 'tabs/types';
import { Permissions } from '../../../../constants';
import {
  getInitialFilterState,
  getCorporateWalletTransactions,
  CorporateWalletTransactionFilters,
} from './api';
import { ExportCsv } from './components';

const PAGE_SIZE = 16;
export const mapTransactionTypeToText = (status: string) => {
  if (status === 'SEND_EXTERNAL' || status === 'SWAP_SOURCE') return 'Sent';
  if (
    status === 'BUY' ||
    status === 'SWAP_DESTINATION' ||
    status === 'RECEIVE_EXTERNAL'
  )
    return 'Received';
  return (
    status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
  ).replaceAll('_', ' ');
};

export const CorporateWallet = () => {
  const styles = useStyles();
  const { palette, spacing } = useTheme();
  const session = useSession();
  const navigate = useNavigate();
  const { merchantId } = useParams();
  const [merchant, setMerchant] = useState({} as Merchant);

  // State for filters persisted in query params
  const [params, setParams] = useSearchParams();
  const [filterState, setFilterState] =
    useState<CorporateWalletTransactionFilters>(getInitialFilterState(params));
  const { page, dateFilterRange } = filterState;
  const initialFilterState: CorporateWalletTransactionFilters = {
    page: 1,
    dateFilterRange: [null, null],
  };
  const handleResetFilters = () => {
    setPage(initialFilterState.page);
    setDateFilterRange(initialFilterState.dateFilterRange);
  };

  const headers = [
    { label: 'Created At', cellKey: 'createdAt', sort: false },
    { label: 'Transaction Id', cellKey: 'transactionId', sort: false },
    { label: 'Type', cellKey: 'type', sort: false },
    { label: 'Amount', cellKey: 'amount', sort: false },
    {
      label: 'Balance Before',
      cellKey: 'balanceAtTransactionTime',
      sort: false,
    },
    { label: 'Balance After', cellKey: 'balanceAfterTransaction', sort: false },
    { label: 'Initiated By', cellKey: 'initiatedBy', sort: false },
  ];
  const [{ error, loading, value: transactionsObject }, fetchTransactions] =
    useAsyncFn(async () => {
      if (!merchantId) return null;
      return getCorporateWalletTransactions({
        merchantId: merchantId,
        pagination: { size: PAGE_SIZE, page },
        filters: {
          createdFrom: dateFilterRange[0],
          createdTo: dateFilterRange[1],
        },
      });
    }, [merchantId, page, filterState]);

  const rows = useMemo(
    () =>
      (transactionsObject?.transactions &&
        transactionsObject.transactions.map((transaction) => ({
          value: {
            id: transaction.id,
            createdAt: new Date(transaction.createdAt).toLocaleString(),
            transactionId: <TruncatedString text={transaction.id} />,
            type: mapTransactionTypeToText(transaction.type),
            amount: (
              <div>
                <Typography variant="bodySmall">
                  {formatCurrency(transaction.amount)}
                </Typography>
                <Typography variant="bodySmallest">
                  {formatCurrency(transaction.amountReference)}
                </Typography>
              </div>
            ),
            initiatedBy: transaction.authorizedBy?.email ?? '-',
            balanceAtTransactionTime: transaction.balanceAtTransactionTime ? (
              <div>
                <Typography variant="bodySmall">
                  {formatCurrency(transaction.balanceAtTransactionTime)}
                </Typography>
                {transaction.balanceReferenceAtTransactionTime && (
                  <Typography variant="bodySmallest">
                    {formatCurrency(
                      transaction.balanceReferenceAtTransactionTime
                    )}
                  </Typography>
                )}
              </div>
            ) : (
              '-'
            ),
            balanceAfterTransaction: transaction.balanceAfterTransaction ? (
              <div>
                <Typography variant="bodySmall">
                  {formatCurrency(transaction.balanceAfterTransaction)}
                </Typography>
                {transaction.balanceReferenceAfterTransaction && (
                  <Typography variant="bodySmallest">
                    {formatCurrency(
                      transaction.balanceReferenceAfterTransaction
                    )}
                  </Typography>
                )}
              </div>
            ) : (
              '-'
            ),
          },
        }))) ??
      [],
    [transactionsObject]
  );

  const setFilterStateFor = <K extends keyof CorporateWalletTransactionFilters>(
    filterName: K,
    isObject: boolean
  ) => {
    return (newValue: CorporateWalletTransactionFilters[K]) => {
      params.set(
        filterName,
        isObject ? JSON.stringify(newValue) : String(newValue)
      );
      setParams(params);
      setFilterState((state) => ({ ...state, [filterName]: newValue }));
    };
  };
  const setPage = setFilterStateFor('page', false);
  const setDateFilterRange = setFilterStateFor('dateFilterRange', true);

  useAsync(async () => {
    if (merchantId && merchantId !== 'new') {
      const m = await getMerchant(merchantId);
      setMerchant(m);
      void fetchTransactions();
      return m;
    }
    return null;
  }, [merchantId]);

  useUpdateEffect(() => {
    void fetchTransactions();
  }, [page, filterState, fetchTransactions]);

  if (
    session.authenticated &&
    !session.permissions.includes(Permissions.VIEW_CORPORATE_WALLET)
  ) {
    void navigate('/merchants');
  }

  return (
    <>
      <Header>
        <div css={styles.headerContent}>
          <div css={styles.titleContainer}>
            <div css={styles.back} onClick={() => navigate('/merchants')}>
              <Typography variant="subtitle" color={palette.grey.x300}>
                Merchants
              </Typography>
            </div>
            <div css={styles.chevron}>
              <Icon name="chevron" size={spacing.xxs} />
            </div>
            <div
              css={styles.back}
              onClick={() => navigate(`/merchants/${merchantId}`)}
            >
              <Typography variant="subtitle" color={palette.grey.x300}>
                {merchant.name}
              </Typography>
            </div>
            <div css={styles.chevron}>
              <Icon name="chevron" size={spacing.xxs} />
            </div>
            <Typography variant="subtitle">Corporate Wallet</Typography>
          </div>
          {merchantId && (
            <ExportCsv
              filters={{
                createdFrom: dateFilterRange[0],
                createdTo: dateFilterRange[1],
              }}
              merchantId={merchantId}
              disabled={
                !!transactionsObject &&
                transactionsObject.transactions.length === 0
              }
            />
          )}
        </div>
      </Header>
      <div css={styles.wrappingContainer}>
        {transactionsObject?.balances && (
          <Balances
            balances={transactionsObject.balances}
            addresses={transactionsObject.addresses}
          />
        )}
        {error && (
          <EmptyContent
            heading="Oops! Something went wrong."
            body="Go back to the Merchant Details."
            action={
              <Button
                text="Go to Merchant Details"
                variant="primary"
                onClick={() => navigate(`/merchants/${merchantId}`)}
              />
            }
          />
        )}

        <>
          <Typography variant="subtitle">Transactions</Typography>
          <div
            css={{
              display: 'grid',
              gridTemplateColumns: 'repeat(10, auto) 1fr',
              gap: spacing.xxxs,
              alignItems: 'center',
              zIndex: 1,
            }}
          >
            <DateInput
              placeholder="All time"
              type="range"
              value={dateFilterRange}
              onChange={(e: [Date | null, Date | null]) => {
                setPage(1);
                setDateFilterRange(e);
              }}
              withTime
            />
            <UtilityButton
              variant="text"
              text="Reset filters"
              onClick={handleResetFilters}
            />
            <UtilityButton
              variant="secondary"
              text="Refresh"
              onClick={fetchTransactions}
              disabled={loading}
            />
            {loading && (
              <Icon
                name="spinner"
                className={{ svg: { color: palette.grey.default } }}
              />
            )}
          </div>
          <div css={styles.tableWrapper}>
            <Table
              headers={headers}
              rows={rows}
              // eslint-disable-next-line @typescript-eslint/no-base-to-string, @typescript-eslint/restrict-template-expressions
              onRowClick={(r) => navigate(`/transactions/${r.id}`)}
            />
            <div css={{ marginTop: spacing.xxs }}>
              <Pagination
                currentPage={page}
                onChange={setPage}
                numberOfElements={transactionsObject?.aggregatedData.count || 0}
                pageSize={PAGE_SIZE}
              />
            </div>
          </div>
        </>
      </div>
    </>
  );
};

const useStyles = createStyles(({ spacing, shape, palette }) => ({
  wrappingContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.xs,
  },
  headerContent: {
    display: 'grid',
    gap: spacing.xxs,
    gridTemplateColumns: '1fr auto auto',
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  back: {
    cursor: 'pointer',
    ':hover': {
      textDecoration: 'underline',
      textDecorationColor: palette.grey.x300,
    },
  },
  chevron: {
    transform: 'rotate(180deg)',
    padding: `0 ${spacing.xxs}`,
    svg: {
      fill: 'none',
      stroke: palette.grey.x300,
    },
  },
  apiKey: {
    li: {
      border: `1px solid ${palette.grey.x200}`,
      borderRadius: shape.borderRadius,
      color: palette.black,
    },
  },
  tableWrapper: {
    border: `1px solid ${palette.grey.x100}`,
    borderRadius: shape.borderRadius,
  },
}));
